import React, { useState } from "react";
import { IconEye, IconEyeOff, IconArrowLeft } from "@tabler/icons-react";
import fetchApi from "../Lib/api";
const ChangePasswordForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const reset_password_token = new URLSearchParams(window.location.search).get(
    "reset_password_token"
  );

  const is_new_password = new URLSearchParams(window.location.search).get(
    "new_password"
  ) === "true";

  const [formData, setFormData] = useState({
    password: "",
    password_confirmation: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return "Password must contain uppercase, lowercase, and number";
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }
    
    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    try {
      if (!validateForm()) return;
      setIsLoading(true);
      const response = await fetchApi("/users/password", "PUT", {
        user: {
          reset_password_token: reset_password_token,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
        },
      });

      if (response.ok) {
        // Handle password change success
        setIsLoading(false);
        window.location.href = is_new_password ? "/onboarding" : "/dashboard/#/";
      } else {
        // Handle password change failure
        console.error("Password change failed.");
      }
    } catch (error) {
      console.error("Error during password change:", error);
    }
    finally{
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthLabels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
  const strengthColors = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#16a34a"];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          {is_new_password && (
            <>
            <p className="text-2xl font-semibold text-blue-600 tracking-wide mb-3">
              Welcome to Nowcollab
            </p>
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-50 rounded-full mb-4 overflow-hidden">
            <img src='/logo3.png' alt="nowcollab-logo" className="w-12 h-12 object-contain"/>
          </div>
          </>
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {is_new_password ? "Set Your Password" : "Change Password"}
          </h1>
          <p className="text-gray-600 text-sm">
            {is_new_password 
              ? "Create a strong password to secure your account" 
              : "Enter a new password to update your account security"
            }
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <div className="space-y-6">
            {/* Password Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  className={`w-full px-4 py-3 pr-12 rounded-lg border-2 transition-colors focus:outline-none ${
                    errors.password 
                      ? "border-red-300 focus:border-red-500" 
                      : "border-gray-200 focus:border-indigo-500"
                  }`}
                  placeholder="Enter your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex space-x-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i < passwordStrength 
                            ? `bg-[${strengthColors[passwordStrength - 1]}]`
                            : "bg-gray-200"
                        }`}
                        style={{
                          backgroundColor: i < passwordStrength 
                            ? strengthColors[passwordStrength - 1]
                            : "#e5e7eb"
                        }}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium`} style={{
                    color: passwordStrength > 0 ? strengthColors[passwordStrength - 1] : "#6b7280"
                  }}>
                    {passwordStrength > 0 ? strengthLabels[passwordStrength - 1] : "Enter password"}
                  </p>
                </div>
              )}
              
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.password_confirmation}
                  onChange={(e) => handleInputChange("password_confirmation", e.target.value)}
                  className={`w-full px-4 py-3 pr-12 rounded-lg border-2 transition-colors focus:outline-none ${
                    errors.password_confirmation 
                      ? "border-red-300 focus:border-red-500" 
                      : "border-gray-200 focus:border-indigo-500"
                  }`}
                  placeholder="Confirm your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password_confirmation && (
                <p className="text-red-500 text-sm mt-1">{errors.password_confirmation}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Password Requirements:</h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : ''}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-2 ${formData.password.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`} />
                  At least 8 characters
                </li>
                <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/[A-Z]/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  One uppercase letter
                </li>
                <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : ''}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/[a-z]/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  One lowercase letter
                </li>
                <li className={`flex items-center ${/\d/.test(formData.password) ? 'text-green-600' : ''}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/\d/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  One number
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || passwordStrength < 4}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 shadow-md ${
                isLoading || passwordStrength < 4
                  ? "bg-gray-300 cursor-not-allowed disabled:opacity-100 disabled:shadow-sm"
                  : "bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 transform hover:scale-[1.02]"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </div>
              ) : (
                `${is_new_password ? "Set" : "Change"} Password`
              )}
            </button>
          </div>
        </div>

        {/* Security Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            🔒 Your password is encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordForm;